import { Chat, ChatSessionResponse, SupplementalResult } from 'src/types/global';
import { ChatLog, Supplement } from './home.types';
import config from '../../config';
import api from '../../services/api';
import { reportError } from '../../utils/rollbar';

const CHAT_SESSION_FIELDS = ['session_name', 'id', 'function_id', 'llm_label', 'llm_config', 'time_created'];

export const getChatSessions = async (
	CHAT_SESSIONS_ENDPOINT: string,
	limit: number,
	lastId: string | null = null,
): Promise<{ chats: Chat[]; sessionCursor: string | null }> => {
	let chatSessions: Chat[] = [];
	const params: Record<string, string> = {
		limit: (limit + 1).toString(),
		'response-fields': CHAT_SESSION_FIELDS.join(' '),
		...(lastId ? { 'last-id': lastId } : {})
	};
	const queryString = new URLSearchParams(params).toString();
	await api
		.get<ChatSessionResponse[]>(`${CHAT_SESSIONS_ENDPOINT}?${queryString}`)
		.then((response) => {
			const newChatSessions = response.data.map((chatSession) => {
				if (typeof chatSession.session_name !== 'string' && chatSession.session_name !== null) {
					reportError(
						'Invalid session name type. Session name must be a string.',
						new Error(
							`Invalid session name type for session ID: ${chatSession.id}. Session name is of type ${typeof chatSession.session_name}.`,
						),
					);
				}

				const sessionName =
					chatSession.session_name === '' ||
					chatSession.session_name === null ||
					typeof chatSession.session_name !== 'string'
						? 'Blank Chat Name'
						: chatSession.session_name;

				return {
					name: sessionName,
					id: chatSession.id,
					llmId: chatSession.function_id,
					llmLabel: chatSession.llm_label,
					llmConfig: chatSession.llm_config || config.DEFAULT_LLM_CONFIG,
					time_created: new Date(chatSession.time_created),
				};
			});
			chatSessions = newChatSessions;
		})
		.catch((error) => {
			reportError(error.message, error);
		});
	return {
		chats: chatSessions.slice(0, limit),
		sessionCursor: chatSessions.length > limit ? chatSessions[limit - 1].id : null
	};
};


export function hasCustomLLMConfig(config, llmId) {
	let functionIds = [
		config.QUERY_OPENAI_LLM_FUNCTION_ID,
		config.QUERY_COHERE_LLM_FUNCTION_ID,
		config.QUERY_ANTHROPIC_LLM_FUNCTION_ID,
		config.QUERY_GOOGLE_LLM_FUNCTION_ID,
	];
	return functionIds.includes(llmId);
}

export function buildChatRequest(chatLog) {
	const mappedMessages = chatLog.map((item) => {
		return [
			{
				role: 'user',
				content: item.chatPrompt,
			},
			{
				role: 'assistant',
				content: item.botMessage,
			},
		];
	});
	// NOTE: The most recent prompt is currently provided outside
	// of the chat history
	return mappedMessages.flat();
}

/**
 * Must be given the chat log from *before* the current turn's optimistic row.
 * Messages without content are dropped: the API requires `content`, and a row
 * can carry a null primary answer.
 */
export function buildSupplementRequest(chatLog: ChatLog[]) {
	return chatLog
		.map((item) => {
			const turn = [
				{ role: 'user', content: item.chatPrompt },
				{ role: 'assistant', content: item.botMessage },
			];
			(item.supplements ?? []).forEach((supplement) => {
				if (supplement.text) {
					turn.push({ role: 'assistant', content: supplement.text });
				}
			});
			return turn;
		})
		.flat()
		.filter((message) => typeof message.content === 'string' && message.content !== '');
}

export interface ParsedSupplement {
	status: 'done' | 'abstained' | 'error';
	text?: string;
	abstained: boolean;
}

export function parseSupplementObservation(observation: unknown): ParsedSupplement {
	if (typeof observation !== 'string') {
		return { status: 'error', abstained: false };
	}

	let parsed;
	try {
		parsed = JSON.parse(observation);
	} catch {
		return { status: 'error', abstained: false };
	}

	const answer = parsed?.answer;
	if (typeof answer !== 'string' || answer.trim() === '') {
		return { status: 'error', abstained: false };
	}

	const abstained = parsed.abstained === true;
	return { status: abstained ? 'abstained' : 'done', text: answer, abstained };
}

export function supplementsFromStoredResults(
	entries: SupplementalResult[] | undefined,
): Supplement[] | undefined {
	if (!Array.isArray(entries) || entries.length === 0) {
		return undefined;
	}

	return entries.map((entry) => {
		const {
			function_id: functionId,
			observation,
			shared_with_customer: sharedWithCustomer,
		} = entry;
		if (entry.abstained) {
			return { functionId, status: 'abstained', text: observation ?? undefined };
		}
		if (typeof observation === 'string' && observation.trim() !== '') {
			return {
				functionId,
				status: 'done',
				text: observation,
				sharedWithCustomer,
			};
		}
		return { functionId, status: 'error' };
	});
}

// The model repeats the title in its output, and the card renders it separately.
export function stripSupplementTitle(text: string, title: string): string {
	const heading = title.replace(/:+$/, '').toLowerCase();
	const consume = (value: string, token: string) =>
		value.startsWith(token) ? value.slice(token.length) : value;

	let rest = consume(text.trimStart(), '**');
	if (!rest.toLowerCase().startsWith(heading)) {
		return text;
	}

	rest = rest.slice(heading.length);
	rest = consume(rest, ':');
	rest = consume(rest, '**');
	rest = consume(rest, ':');
	return rest.trimStart();
}
